import { type ReactNode, useEffect, useState } from 'react';
import { canWriteStaffScope } from '../lib/staff-access.js';
import { Link, useParams } from 'react-router-dom';
import {
  allocateFromStock,
  canEditOrderDelivery,
  canEditOrderDocuments,
  canEditOrderPallets,
  canEditOrderProject,
  canEditOrderProjectName,
  canEditOrderWorkCompletion,
  fromStockSplitSource,
  i18n,
  isOrderTerminal,
  lineStockChip,
  nextOrderStatuses,
  planFulfillment,
  type OrderStatus,
  type StockChip,
} from '@wms/shared';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';
import { formatDate, formatDateTime } from '../lib/format.js';
import { mapsHref, telHref } from '../lib/contact.js';
import { hasOrderContentForDelivery } from '../lib/order-delivery-edit.js';
import { OrderStatusChip } from '../components/StatusChip.js';
import { OrderLinesEditor } from '../components/OrderLinesEditor.js';
import { DeliveryGroupsEditor } from '../components/DeliveryGroupsEditor.js';
import { DeliveryGroupItems } from '../components/DeliveryGroupItems.js';
import { ThumbButton } from '../components/ThumbButton.js';
import { OrderDocuments } from '../components/OrderDocuments.js';
import { ReconcileInvoiceDialog } from '../components/ReconcileInvoiceDialog.js';
import { OrderStageStamp, type OrderStageActor } from '../components/OrderStageStamp.js';
import { OrderPalletPickerDialog } from '../components/OrderPalletPickerDialog.js';
import { OrderPalletPackingDialog } from '../components/OrderPalletPackingDialog.js';
import { TransferOrderPartnerDialog } from '../components/TransferOrderPartnerDialog.js';
import { liveOrderLineStockChip, shouldShowLiveLineStock } from '../lib/order-line-stock.js';
import { canAmendOrderLines } from '../lib/order-amend.js';
import { orderProjectUpdateBody } from '../lib/order-project.js';
import {
  formatOrderCorrectionValue,
  orderCorrectionFieldChanges,
} from '../lib/order-correction-history.js';
import { orderTransitionErrorMessage } from '../lib/order-transition-error.js';
import { ShareOrderDialog } from '../components/ShareOrderDialog.js';
import { OrderComments } from '../components/OrderComments.js';

const { t } = i18n;

interface BomLineRow {
  componentSkuId: string;
  qtyRequired: string;
  component: { id: string; code: string; nameLt: string; photoUrl: string | null };
}

interface OrderLineRow {
  id: string;
  productSkuId: string;
  qty: string;
  comment: string | null;
  unitPriceCents: number | null;
  sortOrder: number;
  stockChipAtSubmit: string | null;
  maxProducibleAtSubmit: number | null;
  fromStockAtSubmit: string | null;
  product: {
    id: string;
    code: string;
    nameLt: string;
    type: 'PRODUCT' | 'COMPONENT';
    photoUrl: string | null;
    bomLines: BomLineRow[];
  };
}

interface DeliveryGroupRow {
  id: string;
  address: string;
  city: string | null;
  street: string | null;
  houseNumber: string | null;
  unitNumber: string | null;
  addressComment: string | null;
  phone: string | null;
  phones: string[];
  preferredDate: string | null;
  workDate: string | null;
  fulfilledDate: string | null;
  status: 'PENDING' | 'OUT_FOR_DELIVERY' | 'DELIVERED' | 'PICKED_UP' | 'FAILED';
  pickupAtWarehouse: boolean;
  deliveryFeeCents: number;
  sortOrder: number;
  lines: { orderLineId: string; qty: string }[];
}

interface OrderDetail {
  requestedWorkDate: string | null;
  id: string;
  code: string;
  status: OrderStatus;
  insufficientParts: boolean;
  partner: { id: string; code: string; name: string; defaultSlaWorkdays: number };
  notes: string | null;
  partnerProjectRef: string | null;
  partnerProjectName: string | null;
  placedAt: string;
  submittedAt: string | null;
  assembledAt: string | null;
  deliveredAt: string | null;
  closedAt: string | null;
  cancelledAt: string | null;
  workCompletionAt: string | null;
  assemblyFeeCents: number;
  partsPassThroughCents: number;
  invoiceNumber: string | null;
  invoiceUrl: string | null;
  invoicePushState: string | null;
  lines: OrderLineRow[];
  deliveryGroups: DeliveryGroupRow[];
  pallets: {
    kind: 'WHOLE_PALLET' | 'OUTPUT';
    pallet: {
      id: string;
      code: string;
      state: string;
      contents: {
        skuId: string;
        remainingQty: string;
        sku: { code: string; nameLt: string };
      }[];
    };
  }[];
  reservations: {
    skuId: string;
    qty: string;
    kind: 'FINISHED' | 'COMPONENT';
    releasedAt: string | null;
    sku: { code: string; nameLt: string; photoUrl: string | null };
  }[];
  changes: {
    isComment?: boolean;
    id: string;
    fromStatus: OrderStatus | null;
    toStatus: OrderStatus;
    reason: string | null;
    timestamp: string;
    actor: OrderStageActor | null;
  }[];
  corrections: {
    id: string;
    timestamp: string;
    reason: string | null;
    before: unknown;
    after: unknown;
    actor: { displayName: string; email: string } | null;
  }[];
}

const correctionFieldKeys: Record<string, string> = {
  requestedWorkDate: 'order.requested_work_date.label',
  workCompletionAt: 'order.corrections.field.work_completion',
  notes: 'order.corrections.field.notes',
  partnerProjectRef: 'order.corrections.field.project_ref',
  partnerProjectName: 'order.corrections.field.project_name',
  lines: 'order.corrections.field.lines',
  deliveryGroups: 'order.corrections.field.delivery_groups',
  documents: 'order.corrections.field.documents',
  assemblyFeeCents: 'order.corrections.field.assembly_fee',
  partsPassThroughCents: 'order.corrections.field.parts_fee',
  wholePalletIds: 'order.corrections.field.whole_pallets',
};

const correctionReasonKeys: Record<string, string> = {
  'set estimated work-completion date': 'order.corrections.reason.work_completion',
  'edited order notes': 'order.corrections.reason.notes',
  'edited order project metadata': 'order.corrections.reason.project',
  'replaced delivery groups': 'order.corrections.reason.delivery_groups',
  'attached order document': 'order.corrections.reason.document_attached',
  'removed order document': 'order.corrections.reason.document_removed',
  'updated pricing/notes/project': 'order.corrections.reason.pricing',
};

interface PartnerStockBalance {
  skuId: string;
  onHand: number;
  reserved: number;
  available: number;
}

export function OrderDetailPage() {
  const [shareOpen, setShareOpen] = useState(false);
  const { id = '' } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [stock, setStock] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [draftSaved, setDraftSaved] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [linesOpen, setLinesOpen] = useState(false);
  const [amendOpen, setAmendOpen] = useState(false);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [palletsOpen, setPalletsOpen] = useState(false);
  const [packingOpen, setPackingOpen] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [partnerTransferOpen, setPartnerTransferOpen] = useState(false);
  const [projectEdit, setProjectEdit] = useState<{ ref: string; name: string } | null>(null);
  // WMS-31: estimated work-completion date. Holds the `datetime-local` string
  // while editing, or null when the inline editor is closed.
  const [workEdit, setWorkEdit] = useState<string | null>(null);
  // WMS-36: order notes. Holds the draft text while editing, null when closed.
  const [notesEdit, setNotesEdit] = useState<string | null>(null);

  async function saveNotes(notes: string) {
    try {
      await api(`/orders/${id}/notes`, { method: 'PUT', body: { notes } });
      setNotesEdit(null);
      setReloadKey((k) => k + 1);
    } catch {
      window.alert(t('common.error'));
    }
  }

  async function saveWorkCompletion(atLocal: string | null, acceptRequested = false) {
    try {
      await api(`/orders/${id}/work-completion`, {
        method: 'PUT',
        // datetime-local is browser-local (the warehouse runs on Vilnius time);
        // send an absolute instant so the server stores UTC unambiguously.
        body: acceptRequested
          ? { acceptRequested: true }
          : { at: atLocal ? new Date(atLocal).toISOString() : null },
      });
      setWorkEdit(null);
      setReloadKey((k) => k + 1);
    } catch {
      window.alert(t('common.error'));
    }
  }

  async function saveProject() {
    if (!projectEdit) return;
    try {
      await api(`/orders/${id}/project`, {
        method: 'PUT',
        body: orderProjectUpdateBody(projectEdit, canEditProjectRef),
      });
      setProjectEdit(null);
      setReloadKey((k) => k + 1);
    } catch {
      window.alert(t('common.error'));
    }
  }

  useEffect(() => {
    setOrder(null);
    api<OrderDetail>(`/orders/${id}`)
      .then(setOrder)
      .catch((err: unknown) => setError(String(err)));
  }, [id, reloadKey]);

  useEffect(() => {
    if (!order) return;
    api<PartnerStockBalance[]>(`/partners/${order.partner.id}/stock/balances`)
      .then((rows) => {
        const m: Record<string, number> = {};
        for (const r of rows) m[r.skuId] = r.available;
        setStock(m);
      })
      .catch(() => setStock({}));
  }, [order, reloadKey]);

  async function submit() {
    setBusy('submit');
    try {
      await api(`/orders/${id}/submit`, { method: 'POST' });
      setReloadKey((k) => k + 1);
    } catch {
      window.alert(t('common.error'));
    } finally {
      setBusy(null);
    }
  }

  async function saveDraft() {
    setBusy('save-draft');
    setDraftSaved(false);
    try {
      await api(`/orders/${id}/save-draft`, { method: 'POST', body: {} });
      setDraftSaved(true);
    } catch {
      window.alert(t('common.error'));
    } finally {
      setBusy(null);
    }
  }

  async function pushInvoice() {
    setBusy('push');
    try {
      const result = await api<{
        status: 'pushed' | 'failed';
        invoiceNumber?: string;
        invoiceUrl?: string;
        error?: string;
      }>(`/orders/${id}/push-invoice`, { method: 'POST' });
      if (result.status !== 'pushed') {
        window.alert(result.error ?? t('billing.push.terminal'));
      }
      setReloadKey((k) => k + 1);
    } catch {
      window.alert(t('common.error'));
    } finally {
      setBusy(null);
    }
  }

  async function transition(toStatus: OrderStatus) {
    let reason: string | null = null;
    if (toStatus === 'CANCELLED') {
      reason = window.prompt(t('order.transition.cancel_prompt')) ?? null;
      if (!reason) return;
    }
    setBusy(toStatus);
    try {
      const path = toStatus === 'CANCELLED' ? `/orders/${id}/cancel` : `/orders/${id}/transition`;
      await api(path, {
        method: 'POST',
        body: toStatus === 'CANCELLED' ? { reason } : { toStatus, ...(reason ? { reason } : {}) },
      });
      setReloadKey((k) => k + 1);
    } catch (transitionError) {
      const skuLabels = new Map<string, string>();
      for (const line of order?.lines ?? []) {
        skuLabels.set(line.product.id, `${line.product.code} / ${line.product.nameLt}`);
        for (const bomLine of line.product.bomLines) {
          skuLabels.set(
            bomLine.componentSkuId,
            `${bomLine.component.code} / ${bomLine.component.nameLt}`,
          );
        }
      }
      for (const link of order?.pallets ?? []) {
        for (const content of link.pallet.contents) {
          skuLabels.set(content.skuId, `${content.sku.code} / ${content.sku.nameLt}`);
        }
      }
      window.alert(orderTransitionErrorMessage(transitionError, skuLabels));
    } finally {
      setBusy(null);
    }
  }

  if (error) return <div className="text-sm text-red-600">{error}</div>;
  if (!order) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;

  const isDraft = order.status === 'DRAFT';
  // Draft availability mirrors the submit-time plan (WMS-21): draw finished
  // units from own stock first, build the remainder from components, and
  // aggregate shared components across ALL lines. This warns about a shortfall
  // (and shows the from-stock/to-build split per product) before submit.
  const plan = planFulfillment(
    order.lines.map((l) => ({
      productSkuId: l.product.id,
      qty: Number(l.qty),
      bom: l.product.bomLines.map((b) => ({
        componentSkuId: b.componentSkuId,
        qtyRequired: Number(b.qtyRequired),
      })),
      lineIsLeaf: l.product.type === 'COMPONENT',
    })),
    stock,
  );
  const splitByProduct = new Map(plan.products.map((p) => [p.productSkuId, p]));
  const shortSkus = new Set(plan.shortages.map((s) => s.skuId));
  // Labels for the shortfall banner (component or leaf-product SKU → code/name).
  const skuLabels: Record<string, { code: string; nameLt: string }> = {};
  for (const l of order.lines) {
    skuLabels[l.product.id] = { code: l.product.code, nameLt: l.product.nameLt };
    for (const b of l.product.bomLines) {
      skuLabels[b.componentSkuId] = { code: b.component.code, nameLt: b.component.nameLt };
    }
  }
  // Partner-wide `available` = onHand − ALL open reservations, so it has this
  // order's OWN reservation already subtracted. Adding that back yields "what
  // this order can still draw" — otherwise a component this order has fully
  // reserved reads as 0 / N (looks short) even though its stock is locked in.
  // Drafts hold no reservations yet, so this is a no-op there.
  const ownReserved: Record<string, number> = {};
  for (const r of order.reservations) {
    if (r.releasedAt) continue;
    ownReserved[r.skuId] = (ownReserved[r.skuId] ?? 0) + Number(r.qty);
  }
  const stockForOrder: Record<string, number> = { ...stock };
  for (const [skuId, qty] of Object.entries(ownReserved)) {
    stockForOrder[skuId] = (stockForOrder[skuId] ?? 0) + qty;
  }
  // Live availability colouring on the BOM/leaf rows only makes sense while this
  // order's reservations are still OPEN — a draft, or reserved (submitted /
  // on-hold / in-assembly). Once assembled the components are consumed and
  // released, so live stock no longer restores them; don't paint already-
  // consumed components as shortages (WMS-26/P2).
  const reservationsOpen =
    order.status === 'SUBMITTED' || order.status === 'ON_HOLD' || order.status === 'IN_ASSEMBLY';
  const liveStockColoring = isDraft || reservationsOpen;

  // Per-line finished-stock draw (WMS-26/P2). New orders carry the snapshot
  // (fromStockAtSubmit); a legacy order still pre-assembly derives it from its
  // open FINISHED reservations; a legacy order already past assembly can't be
  // reconstructed (reservations were bumped/released) — show that explicitly
  // rather than a wrong number.
  const hasSnapshot = order.lines.some((l) => l.fromStockAtSubmit != null);
  const splitSource = fromStockSplitSource(order.status, hasSnapshot, order.submittedAt != null);
  const splitUnavailable = splitSource === 'unavailable';
  const lineFromStock = new Map<string, number>();
  if (splitSource === 'snapshot') {
    for (const l of order.lines) lineFromStock.set(l.id, Number(l.fromStockAtSubmit ?? 0));
  } else if (splitSource === 'reservations') {
    const finished = order.reservations
      .filter((r) => r.releasedAt === null && r.kind === 'FINISHED')
      .map((r) => ({ productSkuId: r.skuId, fromStock: Number(r.qty) }));
    const alloc = allocateFromStock(
      order.lines.map((l) => ({ productSkuId: l.product.id, qty: Number(l.qty) })),
      finished,
    );
    order.lines.forEach((l, i) => lineFromStock.set(l.id, alloc[i] ?? 0));
  }
  // Status changes are internal (Operator/Admin) — a partner may only *see* the
  // status and cancel their own order, never push it to In-assembly / On-hold.
  const roles = canWriteStaffScope(user, 'PARTNER', order.partner.id)
    ? (user?.roles.map((r) => r.role) ?? [])
    : [];
  const canUpdateStatus = roles.includes('ADMIN') || roles.includes('OPERATOR');
  // invoice.push = [ADMIN, ACCOUNTANT] (rbac.matrix.ts) — also gates reconcile.
  const canPushInvoice = roles.includes('ADMIN') || roles.includes('ACCOUNTANT');
  // Partner-tier placers (Order Placer AND Partner Admin) submit/cancel their
  // own orders — mirrors order.submit/order.cancel in rbac.matrix.ts. They
  // still can't run fulfilment status transitions (canUpdateStatus).
  const canCancel =
    canUpdateStatus || roles.includes('ORDER_PLACER') || roles.includes('PARTNER_ADMIN');
  const canSubmit = canCancel;
  const canEditProjectRef = canSubmit && canEditOrderProject(order.status, canUpdateStatus);
  const canEditProject = canSubmit && canEditOrderProjectName(order.status);
  // Partners may correct their own order only while it is still Submitted;
  // warehouse staff retain the broader pre-assembly correction window.
  const canAmend = canAmendOrderLines({
    status: order.status,
    roles,
    invoiceNumber: order.invoiceNumber,
    invoicePushState: order.invoicePushState,
  });
  // Partners may manage documents through PACKING_ON_PALLET (WMS-111). At
  // READY_FOR_SHIPMENT the partner view is read-only; warehouse staff can still
  // correct documents (WMS-103).
  const canEditDocuments = canSubmit && canEditOrderDocuments(order.status, canUpdateStatus);
  // Estimated work-completion date (WMS-31) is warehouse-set (order.status.update)
  // and editable until the order is ready to leave (mirrors the API guard).
  const canEditWorkCompletion = canUpdateStatus && canEditOrderWorkCompletion(order.status);
  // Delivery (PRISTATYMAS) info is correctable by partner order.create roles
  // through PACKING_ON_PALLET and by warehouse staff through
  // READY_FOR_SHIPMENT — but only while every group is still PENDING, since the
  // wholesale replace can't preserve a group that has begun processing
  // (WMS-35, WMS-111 + review P1; mirrors the API guard).
  const deliveryProcessingStarted = order.deliveryGroups.some(
    (g) => g.status !== 'PENDING' || g.workDate !== null || g.fulfilledDate !== null,
  );
  const canEditDelivery =
    canSubmit && canEditOrderDelivery(order.status, canUpdateStatus) && !deliveryProcessingStarted;
  // Notes (Pastabos) are correctable by order.create roles until the order is
  // assembled (WMS-36; mirrors the API guard).
  const canEditNotes =
    canSubmit &&
    ![
      'ASSEMBLED',
      'PACKING_ON_PALLET',
      'READY_FOR_SHIPMENT',
      'DELIVERED',
      'CLOSED',
      'CANCELLED',
    ].includes(order.status);
  const canTransferPartner = isDraft && canUpdateStatus;
  const canPackOnPallets =
    canUpdateStatus && order.status === 'ASSEMBLED' && order.lines.length > 0;
  const next = nextOrderStatuses(order.status)
    .filter((s) => s !== 'SUBMITTED') // submission goes through dedicated `/submit`
    // Partners (no status-update right) only get to cancel.
    .filter((s) => canUpdateStatus || s === 'CANCELLED');

  return (
    <section className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <Link to="/orders" className="text-xs text-muted-foreground hover:underline">
            ← {t('order.list.title')}
          </Link>
          <h2 className="text-xl font-semibold mt-1 font-mono">{order.code}</h2>
          <div className="text-xs text-muted-foreground">
            <Link to={`/partners/${order.partner.id}`} className="hover:underline">
              {order.partner.code} · {order.partner.name}
            </Link>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            <svg
              aria-hidden="true"
              className="mr-1 inline h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <path d="m8.6 10.5 6.8-4M8.6 13.5l6.8 4" />
            </svg>
            {t('order.share.title')}
          </button>
          {shareOpen && (
            <ShareOrderDialog
              key={order.id}
              orderId={order.id}
              onClose={() => setShareOpen(false)}
            />
          )}
          <a
            href={`/api/orders/${order.id}/label.pdf?format=thermal`}
            target="_blank"
            rel="noreferrer"
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('order.label.thermal')}
          </a>
          <a
            href={`/api/orders/${order.id}/label.pdf?format=a4`}
            target="_blank"
            rel="noreferrer"
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('order.label.a4')}
          </a>
          <a
            href={`/api/orders/${order.id}/waybill.pdf`}
            target="_blank"
            rel="noreferrer"
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('order.waybill.pdf')}
          </a>
          <OrderStatusChip status={order.status} />
          {canTransferPartner ? (
            <button
              type="button"
              onClick={() => setPartnerTransferOpen(true)}
              className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
            >
              {t('order.transfer.action')}
            </button>
          ) : null}
          {order.insufficientParts ? (
            <span
              className="inline-flex items-center rounded-full border border-red-300 bg-red-100 px-2 py-0.5 text-xs font-medium text-red-900 dark:border-red-700 dark:bg-red-900/40 dark:text-red-100"
              title={t('order.flag.insufficient_parts')}
            >
              ⚠ {t('order.flag.insufficient_parts_short')}
            </span>
          ) : null}
          {order.status !== 'DRAFT' && order.status !== 'CANCELLED' ? (
            order.invoiceNumber ? (
              order.invoiceUrl ? (
                <a
                  href={order.invoiceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900 hover:underline dark:border-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-100"
                  title={t('billing.push.invoice_number')}
                >
                  {order.invoiceNumber}
                </a>
              ) : (
                <span className="rounded-full border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900 dark:border-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-100">
                  {order.invoiceNumber}
                </span>
              )
            ) : order.invoicePushState === 'AMBIGUOUS' ? (
              <div className="flex items-center gap-2">
                <span
                  className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-100"
                  title={t('order.reconcile.hint')}
                >
                  ⚠ {t('order.reconcile.badge')}
                </span>
                {canPushInvoice ? (
                  <button
                    type="button"
                    onClick={() => setReconcileOpen(true)}
                    className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
                  >
                    {t('order.reconcile.action')}
                  </button>
                ) : null}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => void pushInvoice()}
                disabled={busy === 'push'}
                className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
              >
                {busy === 'push' ? t('common.loading') : t('order.action.push_invoice')}
              </button>
            )
          ) : null}
        </div>
      </div>

      {/* Stage timestamps — every stage, with time, spaced evenly (WMS-27). */}
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 text-sm">
        <Info label={t('order.col.placed_at')}>
          <OrderStageStamp value={order.placedAt} changes={order.changes} status="DRAFT" withTime />
        </Info>
        <Info label={t('order.col.submitted_at')}>
          <OrderStageStamp
            value={order.submittedAt}
            changes={order.changes}
            status="SUBMITTED"
            withTime
          />
        </Info>
        <Info label={t('order.col.assembled_at')}>
          <OrderStageStamp
            value={order.assembledAt}
            changes={order.changes}
            status="ASSEMBLED"
            withTime
          />
        </Info>
        <Info label={t('order.col.ready_for_shipment_at')}>
          <OrderStageStamp
            value={null}
            changes={order.changes}
            status="READY_FOR_SHIPMENT"
            withTime
          />
        </Info>
        <Info label={t('order.col.delivered_at')}>
          <OrderStageStamp
            value={order.deliveredAt}
            changes={order.changes}
            status="DELIVERED"
            withTime
          />
        </Info>
        <Info label={t('order.col.closed_at')}>
          <OrderStageStamp
            value={order.closedAt}
            changes={order.changes}
            status="CLOSED"
            withTime
          />
        </Info>
      </div>

      {/* Estimated work-completion date (WMS-31) — warehouse-set, editable
          until READY_FOR_SHIPMENT. */}
      <div className="rounded border border-border p-3 text-sm">
        <div className="mb-3">
          <span className="text-muted-foreground">{t('order.requested_work_date.label')}: </span>
          <span className="font-medium tabular-nums">{order.requestedWorkDate ?? '—'}</span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {canEditWorkCompletion ? (
            <button
              type="button"
              onClick={() => setWorkEdit(toDateTimeLocal(order.workCompletionAt))}
              className="rounded border border-border px-3 py-1.5 hover:bg-muted"
            >
              {t('order.work_completion.label')}
            </button>
          ) : (
            <span className="text-muted-foreground">{t('order.work_completion.label')}</span>
          )}
          <span className="font-medium tabular-nums">
            {order.workCompletionAt ? formatDateTime(order.workCompletionAt) : '—'}
          </span>
        </div>
        {workEdit !== null ? (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {order.requestedWorkDate ? (
              <button
                type="button"
                onClick={() => void saveWorkCompletion(null, true)}
                className="rounded bg-primary text-primary-foreground px-3 py-1 text-xs"
              >
                {t('order.requested_work_date.accept')}: {order.requestedWorkDate}
              </button>
            ) : null}
            <input
              type="datetime-local"
              value={workEdit}
              onChange={(e) => setWorkEdit(e.target.value)}
              className="rounded border border-border bg-background px-2 py-1.5"
            />
            <button
              type="button"
              onClick={() => setWorkEdit(null)}
              className="rounded border border-border px-3 py-1 text-xs"
            >
              {t('common.cancel')}
            </button>
            {order.workCompletionAt ? (
              <button
                type="button"
                onClick={() => void saveWorkCompletion(null)}
                className="rounded border border-border px-3 py-1 text-xs hover:bg-muted"
              >
                {t('order.work_completion.clear')}
              </button>
            ) : null}
            <button
              type="button"
              disabled={!workEdit}
              onClick={() => void saveWorkCompletion(workEdit || null)}
              className="rounded bg-primary text-primary-foreground px-3 py-1 text-xs disabled:opacity-50"
            >
              {t('common.save')}
            </button>
          </div>
        ) : null}
      </div>

      <div className="rounded border border-border p-3 text-sm">
        <div className="flex items-start justify-between gap-3">
          <div className="grid gap-3 md:grid-cols-2 flex-1">
            <Info label={t('order.col.project_ref')}>{order.partnerProjectRef ?? '—'}</Info>
            <Info label={t('order.col.project_name')}>{order.partnerProjectName ?? '—'}</Info>
          </div>
          {canEditProject ? (
            <button
              type="button"
              onClick={() =>
                setProjectEdit({
                  ref: order.partnerProjectRef ?? '',
                  name: order.partnerProjectName ?? '',
                })
              }
              className="rounded border border-border px-2 py-1 text-xs hover:bg-muted"
            >
              {t('common.edit')}
            </button>
          ) : null}
        </div>
        {projectEdit ? (
          <div className="mt-3 grid gap-2 md:grid-cols-2">
            {canEditProjectRef ? (
              <input
                value={projectEdit.ref}
                onChange={(e) => setProjectEdit({ ...projectEdit, ref: e.target.value })}
                maxLength={120}
                placeholder={t('order.col.project_ref_hint')}
                className="rounded border border-border bg-background px-2 py-1.5"
              />
            ) : null}
            <input
              value={projectEdit.name}
              onChange={(e) => setProjectEdit({ ...projectEdit, name: e.target.value })}
              maxLength={200}
              placeholder={t('order.col.project_name')}
              className={`rounded border border-border bg-background px-2 py-1.5 ${
                canEditProjectRef ? '' : 'md:col-span-2'
              }`}
            />
            <div className="md:col-span-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setProjectEdit(null)}
                className="rounded border border-border px-3 py-1 text-xs"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void saveProject()}
                className="rounded bg-primary text-primary-foreground px-3 py-1 text-xs"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {order.notes || canEditNotes ? (
        <div className="rounded border border-border bg-muted/50 p-3 text-sm space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('order.notes.label')}
            </span>
            {canEditNotes && notesEdit === null ? (
              <button
                type="button"
                onClick={() => setNotesEdit(order.notes ?? '')}
                className="rounded border border-border px-2 py-1 text-xs hover:bg-muted"
              >
                {t('common.edit')}
              </button>
            ) : null}
          </div>
          {notesEdit !== null ? (
            <div className="space-y-2">
              <textarea
                value={notesEdit}
                onChange={(e) => setNotesEdit(e.target.value)}
                rows={4}
                maxLength={2000}
                placeholder={t('order.notes.placeholder')}
                className="w-full rounded border border-border bg-background px-2 py-1.5"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setNotesEdit(null)}
                  className="rounded border border-border px-3 py-1 text-xs"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => void saveNotes(notesEdit)}
                  className="rounded bg-primary text-primary-foreground px-3 py-1 text-xs"
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          ) : (
            <div className="whitespace-pre-wrap">
              {order.notes || (
                <span className="text-muted-foreground">{t('order.notes.empty')}</span>
              )}
            </div>
          )}
        </div>
      ) : null}

      {/* ----- Order lines ----- */}
      <OrderComments
        orderId={order.id}
        changes={order.changes}
        canComment={
          canSubmit &&
          !order.assembledAt &&
          !order.changes.some((c) => !c.isComment && c.toStatus === 'ASSEMBLED') &&
          !isOrderTerminal(order.status) &&
          ['DRAFT', 'SUBMITTED', 'ON_HOLD', 'IN_ASSEMBLY'].includes(order.status)
        }
        onSaved={() => setReloadKey((k) => k + 1)}
      />
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('order.lines.title')}
          </h3>
          {isDraft ? (
            <button
              type="button"
              onClick={() => setLinesOpen(true)}
              className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
            >
              {t('order.lines.edit')}
            </button>
          ) : canAmend ? (
            <button
              type="button"
              onClick={() => setAmendOpen(true)}
              className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
            >
              {t('order.amend.title')}
            </button>
          ) : null}
        </div>
        {isDraft && plan.shortages.length > 0 ? (
          <div className="rounded border border-red-300 bg-red-50 p-3 text-xs text-red-700">
            <p className="font-medium">{t('order.draft.insufficient_title')}</p>
            <p className="mt-1 text-red-600">{t('order.draft.insufficient_hint')}</p>
            <ul className="mt-2 space-y-0.5">
              {plan.shortages.map((r) => (
                <li key={r.skuId} className="tabular-nums">
                  <span className="font-mono">{skuLabels[r.skuId]?.code ?? r.skuId}</span>
                  {skuLabels[r.skuId] ? ` — ${skuLabels[r.skuId]!.nameLt}` : ''}:{' '}
                  {t('order.draft.need')} {r.required}, {t('order.draft.have')} {r.available}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {order.lines.length === 0 ? (
          <div className="rounded border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            {t('order.lines.empty')}
          </div>
        ) : (
          <div className="space-y-2">
            {order.lines.map((line) => {
              const lineQty = Number(line.qty);
              const split = splitByProduct.get(line.product.id);
              // Component needs cover only the to-build remainder (finished units
              // come from stock). On a draft use the live plan; on a submitted
              // order use the persisted submit-time split — never recompute from
              // live stock, which may have moved since submission (WMS-26/P2).
              const buildQty = isDraft
                ? split
                  ? split.toBuild
                  : lineQty
                : Math.max(0, lineQty - (lineFromStock.get(line.id) ?? 0));
              const draftTone: StockChip =
                shortSkus.has(line.product.id) ||
                line.product.bomLines.some((b) => shortSkus.has(b.componentSkuId))
                  ? 'red'
                  : 'green';
              return (
                <div key={line.id} className="rounded border border-border p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <ThumbButton
                        url={line.product.photoUrl}
                        alt={`${line.product.code} — ${line.product.nameLt}`}
                      />
                      <div>
                        <span className="font-mono text-xs text-muted-foreground mr-2">
                          {line.product.code}
                        </span>
                        <span className="font-medium">{line.product.nameLt}</span>
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                          {t(`sku.type.${line.product.type}`)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="tabular-nums">× {lineQty.toLocaleString('lt-LT')}</span>
                      {shouldShowLiveLineStock(order.status) ? (
                        <LineChip
                          line={line}
                          stock={stockForOrder}
                          liveDraft={isDraft}
                          draftTone={draftTone}
                          fromStockQty={lineFromStock.get(line.id) ?? 0}
                        />
                      ) : null}
                    </div>
                  </div>
                  {/* Draft split: how many units come from ready-made stock vs
                      get assembled from components (WMS-21). */}
                  {isDraft &&
                  !lineIsLeaf(line) &&
                  (splitByProduct.get(line.product.id)?.fromStock ?? 0) > 0 ? (
                    <div className="mt-2 text-xs text-muted-foreground tabular-nums">
                      {t('order.draft.from_stock')}:{' '}
                      <span className="font-medium">
                        {splitByProduct.get(line.product.id)!.fromStock}
                      </span>
                      {' · '}
                      {t('order.draft.to_build')}:{' '}
                      <span className="font-medium">
                        {splitByProduct.get(line.product.id)!.toBuild}
                      </span>
                    </div>
                  ) : null}
                  {/* Leaf lines (Component SKU, or a product with no recipe) —
                      no BOM to expand, show the direct own-stock chip. */}
                  {lineIsLeaf(line) ? (
                    <div className="mt-2 text-xs text-muted-foreground">
                      {t(
                        line.product.type === 'COMPONENT'
                          ? 'order.lines.component_leaf'
                          : 'order.lines.product_leaf',
                      )}
                      :{' '}
                      {liveStockColoring ? (
                        <span
                          className={chipTextClass(
                            lineStockChip({
                              required: lineQty,
                              available: stockForOrder[line.product.id],
                            }),
                          )}
                        >
                          {stockForOrder[line.product.id] ?? 0} / {lineQty}
                        </span>
                      ) : (
                        // Post-assembly: components are consumed/shipped — show the
                        // historical quantity, not a live shortage (WMS-26/P2).
                        <span className="tabular-nums">{lineQty}</span>
                      )}
                    </div>
                  ) : null}
                  {/* BOM expansion read-only */}
                  {line.product.bomLines.length > 0 ? (
                    <div className="mt-2 grid gap-1">
                      {line.product.bomLines.map((bom) => {
                        const need = buildQty * Number(bom.qtyRequired);
                        const have = stockForOrder[bom.componentSkuId];
                        const tone = lineStockChip({ required: need, available: have });
                        return (
                          <div
                            key={bom.componentSkuId}
                            className="flex items-center justify-between text-xs"
                          >
                            <span className="flex min-w-0 items-center gap-2 text-muted-foreground">
                              <ThumbButton
                                url={bom.component.photoUrl}
                                alt={`${bom.component.code} — ${bom.component.nameLt}`}
                              />
                              <span>
                                <span className="font-mono">{bom.component.code}</span>
                                <span className="ml-2">{bom.component.nameLt}</span>
                              </span>
                            </span>
                            <span className="flex items-center gap-2 tabular-nums">
                              {splitUnavailable ? (
                                // Legacy order past assembly — the historical
                                // built/consumed split can't be reconstructed.
                                <span className="text-muted-foreground italic">
                                  {t('order.split_unavailable')}
                                </span>
                              ) : liveStockColoring ? (
                                <span className={chipTextClass(tone)}>
                                  {have ?? 0} / {need}
                                </span>
                              ) : (
                                // Post-assembly: consumed — show the quantity, no
                                // live shortage colouring (WMS-26/P2).
                                <span className="text-muted-foreground">{need}</span>
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ----- Whole pallets ----- */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('order.pallets.title')}
          </h3>
          {canSubmit && canEditOrderPallets(order.status) ? (
            <button
              type="button"
              onClick={() => setPalletsOpen(true)}
              className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
            >
              {t('order.pallets.edit')}
            </button>
          ) : null}
        </div>
        {order.pallets.length === 0 ? (
          <div className="rounded border border-dashed border-border p-3 text-sm text-muted-foreground">
            {t('order.pallets.empty')}
          </div>
        ) : (
          <ul className="space-y-2">
            {order.pallets.map((link) => (
              <li key={link.pallet.id} className="rounded border border-border p-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <Link
                    to={`/pallets/${link.pallet.id}`}
                    className="font-mono font-medium hover:underline"
                  >
                    {link.pallet.code}
                  </Link>
                  <span className="text-xs text-muted-foreground">
                    {t(`pallet.state.${link.pallet.state}`)}
                  </span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {link.pallet.contents
                    .map(
                      (line) =>
                        `${line.sku.code} × ${Number(line.remainingQty).toLocaleString('lt-LT')}`,
                    )
                    .join(' · ')}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ----- Delivery groups ----- */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('order.groups.title')}
          </h3>
          {canEditDelivery ? (
            <button
              type="button"
              onClick={() => setGroupsOpen(true)}
              disabled={!hasOrderContentForDelivery(order)}
              className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
            >
              {t('order.groups.edit')}
            </button>
          ) : null}
        </div>
        {order.deliveryGroups.length === 0 ? (
          <div className="rounded border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            {t('order.groups.empty')}
          </div>
        ) : (
          <div className="space-y-2">
            {order.deliveryGroups.map((g) => {
              // WMS-38/71: on the warehouse floor these are acted on, not read.
              // A pickup group may still carry an address, and any safe address
              // opens navigation. Unusable values fall back to plain text.
              const mapUrl = mapsHref(g.address);
              const phones = g.phones.length > 0 ? g.phones : g.phone ? [g.phone] : [];
              return (
                <div key={g.id} className="rounded border border-border p-3 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      {g.pickupAtWarehouse ? (
                        <div className="font-medium">📦 {t('order.groups.pickup')}</div>
                      ) : null}
                      {g.address ? (
                        <div className="font-medium whitespace-pre-line">
                          {mapUrl ? (
                            <a
                              href={mapUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={t('order.groups.navigate')}
                              className="text-primary underline decoration-dotted underline-offset-2 hover:decoration-solid"
                            >
                              {g.address}
                            </a>
                          ) : (
                            g.address
                          )}
                        </div>
                      ) : null}
                      {g.addressComment ? (
                        <div className="text-xs text-muted-foreground">
                          <span className="font-medium">{t('order.groups.address_comment')}:</span>{' '}
                          {g.addressComment}
                        </div>
                      ) : null}
                      {phones.map((phone) => {
                        const telUrl = telHref(phone);
                        return (
                          <div key={phone} className="mt-1">
                            <span className="text-xs text-muted-foreground">
                              {t('order.groups.phone')}:{' '}
                            </span>
                            {telUrl ? (
                              <a
                                href={telUrl}
                                title={t('order.groups.call')}
                                className="text-base font-medium text-primary underline decoration-dotted underline-offset-2 hover:decoration-solid"
                              >
                                {phone}
                              </a>
                            ) : (
                              <span className="text-base font-medium">{phone}</span>
                            )}
                          </div>
                        );
                      })}
                      <div className="text-xs text-muted-foreground">
                        {t('order.groups.preferred')}: {formatDate(g.preferredDate)}
                        {g.workDate
                          ? ` · ${t('order.groups.work_date')}: ${formatDate(g.workDate)}`
                          : ''}
                      </div>
                      <DeliveryGroupItems
                        groupCount={order.deliveryGroups.length}
                        lines={g.lines}
                        orderLines={order.lines}
                      />
                    </div>
                    <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-xs">
                      {t(`order.groups.status.${g.status}`)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ----- Documents (WMS-28) ----- */}
      <OrderDocuments orderId={order.id} canEdit={canEditDocuments} reloadKey={reloadKey} />

      {/* ----- Lifecycle ----- */}
      {isDraft ? (
        <div className="rounded border border-border p-3 flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm">
            {draftSaved ? t('order.draft.saved') : t('order.submit.hint')}
          </span>
          <div className="flex flex-wrap gap-2">
            {canCancel ? (
              <button
                type="button"
                onClick={() => void transition('CANCELLED')}
                disabled={busy !== null}
                className="rounded border border-border px-3 py-1.5 text-sm disabled:opacity-50"
              >
                {t('order.action.cancel')}
              </button>
            ) : null}
            {canSubmit ? (
              <>
                <button
                  type="button"
                  onClick={() => void saveDraft()}
                  disabled={busy !== null}
                  className="rounded border border-border px-3 py-1.5 text-sm disabled:opacity-50"
                >
                  {busy === 'save-draft' ? t('common.loading') : t('order.action.save_draft')}
                </button>
                <button
                  type="button"
                  onClick={() => void submit()}
                  disabled={
                    busy !== null || (order.lines.length === 0 && order.pallets.length === 0)
                  }
                  className="rounded bg-primary text-primary-foreground px-3 py-1.5 text-sm disabled:opacity-50"
                >
                  {busy === 'submit' ? t('common.loading') : t('order.action.submit')}
                </button>
              </>
            ) : null}
          </div>
        </div>
      ) : isOrderTerminal(order.status) ? (
        <div className="text-sm text-muted-foreground">{t('order.transition.terminal')}</div>
      ) : next.length === 0 ? null : (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('order.transition.title')}
          </h3>
          <div className="flex flex-wrap gap-2">
            {canPackOnPallets ? (
              <button
                type="button"
                onClick={() => setPackingOpen(true)}
                disabled={busy !== null}
                className="rounded border border-primary px-3 py-1.5 text-sm text-primary hover:bg-muted disabled:opacity-50"
              >
                {t('order.pack.action')}
              </button>
            ) : null}
            {next.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => void transition(s)}
                disabled={busy === s}
                className={`rounded border px-3 py-1.5 text-sm disabled:opacity-50 ${
                  s === 'CANCELLED'
                    ? 'border-amber-500 text-amber-900 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-900/30'
                    : 'border-border hover:bg-muted'
                }`}
              >
                {busy === s ? '…' : `→ ${t(`order.status.${s}`)}`}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ----- Reservations summary ----- */}
      {order.reservations.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('order.reservations.title')}
          </h3>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('order.reservations.col.sku')}</th>
                  <th className="px-3 py-2 font-medium text-right">
                    {t('order.reservations.col.qty')}
                  </th>
                  <th className="px-3 py-2 font-medium">{t('order.reservations.col.state')}</th>
                </tr>
              </thead>
              <tbody>
                {order.reservations.map((r) => (
                  <tr key={`${r.skuId}:${r.kind}`} className="border-t border-border">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span>
                          <span className="font-mono text-xs">{r.sku.code}</span>
                          <span className="ml-2 text-xs text-muted-foreground">{r.sku.nameLt}</span>
                        </span>
                        <ThumbButton url={r.sku.photoUrl} alt={`${r.sku.code} — ${r.sku.nameLt}`} />
                      </div>
                    </td>
                    <td className="px-3 py-2 tabular-nums text-right">
                      {Number(r.qty).toLocaleString('lt-LT')}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.releasedAt
                        ? `${t('order.reservations.released')} · ${formatDate(r.releasedAt)}`
                        : t('order.reservations.open')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* ----- History / amendments (comments visible on the order) ----- */}
      {order.changes.some((c) => c.reason) ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('order.history.title')}
          </h3>
          <ul className="space-y-1">
            {order.changes
              .filter((c) => c.reason && !c.isComment)
              .map((c) => (
                <li key={c.id} className="rounded border border-border px-3 py-2 text-sm">
                  <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span>
                      {c.fromStatus === c.toStatus
                        ? t('order.history.amended')
                        : `${c.fromStatus ? t(`order.status.${c.fromStatus}`) : '—'} → ${t(`order.status.${c.toStatus}`)}`}
                    </span>
                    <span>{formatDate(c.timestamp)}</span>
                  </div>
                  <div className="mt-1">{c.reason}</div>
                </li>
              ))}
          </ul>
        </div>
      ) : null}

      {/* WMS-76 — immutable corrections made after the order was submitted. */}
      {order.corrections.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('order.corrections.title')}
          </h3>
          <ul className="space-y-2">
            {order.corrections.map((correction) => {
              const fields = orderCorrectionFieldChanges(correction.before, correction.after);
              const actorName =
                correction.actor?.displayName.trim() ||
                correction.actor?.email ||
                t('order.corrections.system_actor');
              const reasonKey = correction.reason
                ? correctionReasonKeys[correction.reason]
                : undefined;
              return (
                <li key={correction.id} className="rounded border border-border px-3 py-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span>
                      {t('order.corrections.changed_by')}: {actorName}
                    </span>
                    <span>{formatDateTime(correction.timestamp)}</span>
                  </div>
                  {correction.reason ? (
                    <div className="mt-1 font-medium">
                      {reasonKey ? t(reasonKey) : correction.reason}
                    </div>
                  ) : null}
                  <div className="mt-2 overflow-x-auto rounded border border-border">
                    <table className="min-w-full text-left text-xs">
                      <thead className="bg-muted/40 text-muted-foreground">
                        <tr>
                          <th className="px-2 py-1.5">{t('order.corrections.field')}</th>
                          <th className="px-2 py-1.5">{t('order.corrections.before')}</th>
                          <th className="px-2 py-1.5">{t('order.corrections.after')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fields.map((field) => (
                          <tr key={field.field} className="border-t border-border align-top">
                            <th className="px-2 py-1.5 font-medium">
                              {correctionFieldKeys[field.field]
                                ? t(correctionFieldKeys[field.field]!)
                                : field.field}
                            </th>
                            <td className="max-w-sm whitespace-pre-wrap px-2 py-1.5">
                              {formatOrderCorrectionValue(
                                field.field,
                                field.before,
                                user?.locale === 'en' ? 'en-GB' : 'lt-LT',
                              )}
                            </td>
                            <td className="max-w-sm whitespace-pre-wrap px-2 py-1.5">
                              {formatOrderCorrectionValue(
                                field.field,
                                field.after,
                                user?.locale === 'en' ? 'en-GB' : 'lt-LT',
                              )}
                            </td>
                          </tr>
                        ))}
                        {fields.length === 0 ? (
                          <tr className="border-t border-border">
                            <td className="px-2 py-1.5 text-muted-foreground" colSpan={3}>
                              {t('order.corrections.legacy_detail_unavailable')}
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {linesOpen ? (
        <OrderLinesEditor
          orderId={order.id}
          partnerId={order.partner.id}
          existing={order.lines.map((l) => ({
            productSkuId: l.productSkuId,
            qty: Number(l.qty),
            comment: l.comment ?? '',
            sortOrder: l.sortOrder,
          }))}
          onClose={() => setLinesOpen(false)}
          onSaved={() => {
            setLinesOpen(false);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
      {amendOpen ? (
        <OrderLinesEditor
          amend
          orderId={order.id}
          partnerId={order.partner.id}
          existing={order.lines.map((l) => ({
            productSkuId: l.productSkuId,
            qty: Number(l.qty),
            comment: l.comment ?? '',
            sortOrder: l.sortOrder,
          }))}
          onClose={() => setAmendOpen(false)}
          onSaved={() => {
            setAmendOpen(false);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
      {groupsOpen ? (
        <DeliveryGroupsEditor
          orderId={order.id}
          orderLines={order.lines.map((l) => ({
            id: l.id,
            qty: Number(l.qty),
            label: `${l.product.code} × ${Number(l.qty).toLocaleString('lt-LT')}`,
          }))}
          existing={order.deliveryGroups.map((g) => ({
            address: g.address,
            city: g.city ?? '',
            street: g.street ?? '',
            houseNumber: g.houseNumber ?? '',
            unitNumber: g.unitNumber ?? '',
            addressComment: g.addressComment ?? '',
            phone: g.phone ?? '',
            phones: g.phones,
            preferredDate: g.preferredDate ?? '',
            pickupAtWarehouse: g.pickupAtWarehouse,
            deliveryFeeCents: g.deliveryFeeCents,
            sortOrder: g.sortOrder,
            lines: g.lines.map((l) => ({ orderLineId: l.orderLineId, qty: Number(l.qty) })),
          }))}
          onClose={() => setGroupsOpen(false)}
          onSaved={() => {
            setGroupsOpen(false);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}

      {palletsOpen ? (
        <OrderPalletPickerDialog
          orderId={order.id}
          existingIds={order.pallets
            .filter((link) => link.kind === 'WHOLE_PALLET')
            .map((link) => link.pallet.id)}
          onClose={() => setPalletsOpen(false)}
          onSaved={() => {
            setPalletsOpen(false);
            setReloadKey((key) => key + 1);
          }}
        />
      ) : null}

      {packingOpen ? (
        <OrderPalletPackingDialog
          orderId={order.id}
          lines={order.lines.map((line) => ({
            id: line.id,
            qty: Number(line.qty),
            label: `${line.product.code} — ${line.product.nameLt}`,
          }))}
          onClose={() => setPackingOpen(false)}
          onPacked={() => {
            setPackingOpen(false);
            setReloadKey((key) => key + 1);
          }}
        />
      ) : null}

      {reconcileOpen ? (
        <ReconcileInvoiceDialog
          orderId={order.id}
          onClose={() => setReconcileOpen(false)}
          onReconciled={() => {
            setReconcileOpen(false);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}

      {partnerTransferOpen ? (
        <TransferOrderPartnerDialog
          orderId={order.id}
          currentPartnerId={order.partner.id}
          onClose={() => setPartnerTransferOpen(false)}
          onTransferred={() => {
            setPartnerTransferOpen(false);
            setReloadKey((key) => key + 1);
          }}
        />
      ) : null}
    </section>
  );
}

// A line consumes its own SKU (no BOM walk) when it's a Component, or a
// finished product with no recipe — mirrors expandOrderBom on the API.
function lineIsLeaf(line: OrderLineRow): boolean {
  return line.product.type === 'COMPONENT' || line.product.bomLines.length === 0;
}

function LineChip({
  line,
  stock,
  liveDraft,
  draftTone,
  fromStockQty,
}: {
  line: OrderLineRow;
  stock: Record<string, number>;
  liveDraft: boolean;
  draftTone?: StockChip;
  fromStockQty: number;
}) {
  // Live draft: the fulfillment plan already accounts for finished-stock-first
  // and shared components — follow its verdict (WMS-21).
  if (liveDraft && draftTone) {
    return <ChipPill tone={draftTone} />;
  }
  return (
    <ChipPill
      tone={liveOrderLineStockChip({
        productSkuId: line.product.id,
        lineQty: Number(line.qty),
        fromStockQty,
        lineIsLeaf: lineIsLeaf(line),
        bom: line.product.bomLines.map((bom) => ({
          componentSkuId: bom.componentSkuId,
          qtyRequired: Number(bom.qtyRequired),
        })),
        available: stock,
      })}
    />
  );
}

function ChipPill({ tone }: { tone: StockChip }) {
  const cls =
    tone === 'green'
      ? 'bg-green-100 text-green-900 border-green-300 dark:bg-green-900/40 dark:text-green-100'
      : tone === 'amber'
        ? 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/40 dark:text-amber-100'
        : tone === 'red'
          ? 'bg-red-100 text-red-900 border-red-300 dark:bg-red-900/40 dark:text-red-100'
          : 'bg-muted text-muted-foreground border-border';
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 ${cls}`}>
      {t(`order.line.chip.${tone}`)}
    </span>
  );
}

function chipTextClass(tone: StockChip): string {
  if (tone === 'red') return 'text-red-600';
  if (tone === 'amber') return 'text-amber-700 dark:text-amber-300';
  if (tone === 'green') return 'text-green-700 dark:text-green-300';
  return '';
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div>{children}</div>
    </div>
  );
}

/**
 * Format a stored ISO instant as a `datetime-local` input value
 * (`YYYY-MM-DDTHH:mm`) in the browser's local time, or '' when unset (WMS-31).
 */
function toDateTimeLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}
